import React from 'react';

import { usePrettier, useCodeMirror } from './Sandbox.helpers';
import { useLogs } from './Console/Console.helpers';
import type { CodeEditorContextValue } from './Sandbox.types';

const noop = () => {};
const CodeEditorContext = React.createContext<CodeEditorContextValue>(
  {
    // Initial values just for TypeScript
    logs: [],
    clearLogs: noop,
    showLineNumbers: false,
    setShowLineNumbers: noop,
    formatCodeWithOffset: noop,
    editorWrapperRef: { current: null },
    codeMirrorGrabberRef: { current: null },
    codeMirrorInstance: null,
  }
);

interface Props {
  showLineNumbers: boolean;
  children: React.ReactNode;
}

export function CodeEditorProvider({
  showLineNumbers: initialShowLineNumbers,
  children,
}: Props) {
  const [showLineNumbers, setShowLineNumbers] = React.useState(
    initialShowLineNumbers
  );

  const [logs, clearLogs] = useLogs();
  const editorWrapperRef = React.useRef<HTMLDivElement>(null);

  const handleFormat = usePrettier({
    showLineNumbers,
    editorWrapperRef,
  });

  const { codeMirrorGrabberRef, codeMirrorInstance } =
    useCodeMirror();

  const formatCodeWithOffset = React.useCallback(
    async function formatCodeWithOffset() {
      if (!codeMirrorInstance) {
        console.error(
          'CodeMirror not instantiated yet; cannot format'
        );
        return;
      }

      // If there's a code error, this whole thing throws
      // an error, which is a jarring experience!
      try {
        // For the best user experience, we want to move the cursor
        // along with the formatted code.
        // We pass the current offset to Prettier, and it returns
        // the new offset, based on its best guess about where the
        // cursor should go.
        const cursorOffset =
          codeMirrorInstance.state?.selection?.ranges?.[0]?.from;

        // The `handleFormat` method internally updates the code
        const newCursorOffset = await handleFormat(cursorOffset);

        // We'll reset the cursor position here
        codeMirrorInstance?.dispatch({
          selection: { anchor: newCursorOffset },
        });
      } catch (err) {
        console.error('Error formatting code:', err);
      }
    },
    [handleFormat, codeMirrorInstance]
  );

  React.useEffect(() => {
    if (!codeMirrorInstance) {
      return;
    }

    const handleKeydown = (ev: KeyboardEvent) => {
      if (ev.metaKey && ev.key === 's') {
        ev.preventDefault();
        ev.stopPropagation();
        formatCodeWithOffset();
      }
    };

    codeMirrorInstance.dom.addEventListener('keydown', handleKeydown);

    return () => {
      codeMirrorInstance.dom.removeEventListener(
        'keydown',
        handleKeydown
      );
    };
  }, [formatCodeWithOffset, codeMirrorInstance]);

  const providedValue = React.useMemo(
    () => ({
      logs,
      clearLogs,
      showLineNumbers,
      setShowLineNumbers,
      formatCodeWithOffset,
      editorWrapperRef,
      codeMirrorGrabberRef,
      codeMirrorInstance,
    }),
    [
      logs,
      clearLogs,
      showLineNumbers,
      formatCodeWithOffset,
      codeMirrorGrabberRef,
      codeMirrorInstance,
    ]
  );

  return (
    <CodeEditorContext.Provider value={providedValue}>
      {children}
    </CodeEditorContext.Provider>
  );
}

export default CodeEditorContext;
